import DOMPurify from 'dompurify';
import { useMemo } from 'react';
import { cn } from '@/lib/utils';

/**
 * Renders the HTML body of a NotificationModel.message through DOMPurify
 * before injecting via `dangerouslySetInnerHTML`.
 *
 * **Why dompurify?** Notification message bodies arrive from the backend
 * as raw HTML (Flutter uses `flutter_html` package per
 * `notification_center_row_widget.dart:81-90`). Server-trust is not a
 * substitute for sanitization — DOMPurify strips `<script>`, inline
 * `on*` handlers, `javascript:` URLs, etc. Industry standard;
 * orchestrator approved (Q-E of CONVERSION_NOTES §13).
 *
 * **Allowlist scoping**: default DOMPurify config allows safe inline tags
 * (`<b>`, `<i>`, `<strong>`, `<em>`, `<br>`, `<a>`, `<span>`, `<p>`).
 * No `<img>` / `<iframe>` / forms — notification bodies should never
 * carry those.
 */
interface Props {
  /** Raw HTML message from `NotificationModel.message`. */
  html: string;
  /** Inverted color when rendered on a dark/push variant background. */
  inverted?: boolean;
}

export function NotificationHtmlMessage({ html, inverted = false }: Props) {
  const sanitized = useMemo(
    () =>
      DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
          'b',
          'i',
          'strong',
          'em',
          'br',
          'a',
          'span',
          'p',
          'u',
        ],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
        ALLOW_DATA_ATTR: false,
      }),
    [html],
  );

  return (
    <div
      className={cn(
        '[font:var(--type-body)] [&_a]:underline [&_b]:font-semibold [&_strong]:font-semibold',
        inverted
          ? 'text-white [&_a]:text-white'
          : 'text-[var(--ink-secondary)] [&_a]:text-[var(--accent-primary)] [&_b]:text-[var(--ink-body)] [&_strong]:text-[var(--ink-body)]',
      )}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}
